import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardApi } from '../services/api';
import { AuthApi } from '../services/auth';
import '../App.css';

const LoginPage = () => {
  const { login, applySession, isAuthenticated, loading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const inviteToken = new URLSearchParams(location.search).get('invite_token')?.trim() || '';
  const partnerDemoSessionToken = new URLSearchParams(location.search).get('partner_demo_session')?.trim() || '';
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState(null);
  const [bootstrappingPartnerDemo, setBootstrappingPartnerDemo] = useState(false);
  const inviteAcceptedRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      if (!inviteToken && !partnerDemoSessionToken) {
        const redirectTo = location.state?.from?.pathname || '/';
        navigate(redirectTo, { replace: true });
      }
    }
  }, [isAuthenticated, inviteToken, location.state, navigate, partnerDemoSessionToken]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrapPartnerDemoSession() {
      if (!partnerDemoSessionToken) return;
      setBootstrappingPartnerDemo(true);
      setFormError(null);
      clearError();
      try {
        const session = await AuthApi.exchangePartnerDemoSession(partnerDemoSessionToken);
        applySession(session);
        if (!cancelled) {
          navigate('/bots', { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          const sessionErrorCode = err?.code || err?.details?.code || null;
          if (sessionErrorCode === 'TOKEN_EXPIRED' || sessionErrorCode === 'TOKEN_CONSUMED') {
            setFormError('Demo-lenken har utløpt eller er allerede brukt.');
          } else if (sessionErrorCode === 'DEMO_ACCESS_EXPIRED' || sessionErrorCode === 'DEMO_ACCESS_REVOKED') {
            setFormError('Demo-tilgangen er utløpt eller tilbakekalt.');
          } else {
            setFormError(err.message || 'Demo-sesjonen er ugyldig eller utløpt.');
          }
        }
      } finally {
        if (!cancelled) {
          setBootstrappingPartnerDemo(false);
        }
      }
    }

    bootstrapPartnerDemoSession();
    return () => {
      cancelled = true;
    };
  }, [applySession, clearError, navigate, partnerDemoSessionToken]);

  useEffect(() => {
    let cancelled = false;

    async function acceptInvite() {
      if (!isAuthenticated) return;
      if (!inviteToken) return;
      if (inviteAcceptedRef.current) return;
      try {
        await DashboardApi.acceptPartnerInvite(inviteToken);
        inviteAcceptedRef.current = true;
        if (!cancelled) {
          navigate('/invite/company', { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          setFormError(err.message || 'Kunne ikke godta invitasjonen.');
        }
      }
    }

    acceptInvite();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, inviteToken, navigate]);

  useEffect(() => {
    if (formError) setFormError(null);
    if (error) clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, password]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (bootstrappingPartnerDemo) {
      return;
    }
    if (!identifier || !password) {
      setFormError('Skriv inn brukernavn og passord.');
      return;
    }

    try {
      await login(identifier.trim(), password);
      if (inviteToken) {
        await DashboardApi.acceptPartnerInvite(inviteToken);
        inviteAcceptedRef.current = true;
        navigate('/invite/company', { replace: true });
        return;
      }
      const redirectTo = location.state?.from?.pathname || '/';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setFormError(err.message || 'Kunne ikke logge inn.');
    }
  };

  return (
    <div className="auth-layout">
      <div className="auth-card">
        <header className="auth-header">
          <span className="auth-badge">KI Chat</span>
          <h1>Logg inn</h1>
          <p>Få tilgang til dashbordet og administrer dine KI-boter.</p>
        </header>
        <form className="auth-form" onSubmit={handleSubmit}>
          {partnerDemoSessionToken ? <p className="muted">Logger inn demo-sesjon…</p> : null}
          <label className="auth-label" htmlFor="identifier">
            E-post eller brukernavn
            <input
              id="identifier"
              type="text"
              className="input"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              autoComplete="username"
              placeholder="fornavn@dinbedrift.no"
            />
          </label>
          <label className="auth-label" htmlFor="password">
            Passord
            <input
              id="password"
              type="password"
              className="input"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="********"
            />
          </label>
          {(formError || error) && (
            <div className="auth-error">⚠️ {formError || error}</div>
          )}
          <button type="submit" className="button button-primary" disabled={loading || bootstrappingPartnerDemo}>
            {loading || bootstrappingPartnerDemo ? 'Logger inn…' : 'Logg inn'}
          </button>
          <Link to={inviteToken ? `/registrer?invite_token=${encodeURIComponent(inviteToken)}` : '/registrer'} className="button auth-register-button">
            Registrer konto
          </Link>
        </form>
        <footer className="auth-footer">
          <p>
            <Link to="/forgot-password">Glemt passord?</Link>
          </p>
          <p>
            Problemer med innloggingen? Kontakt{' '}
            <a href="mailto:hei@kichat.no">hei@kichat.no</a>.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default LoginPage;
